import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CircleUserRound, Plus, Scissors, UserRoundMinus, UsersRound } from 'lucide-react';

import { orgQueryOptions } from '@/features/identity';
import type { OrgRef } from '@/features/identity';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, useToastStore } from '@/shared/ui';

import { accountStateClass, accountStateKey } from '../model/accountState';
import type { ElementDetail, OwnerView } from '../model/types';
import { useOwnerActions, useSetOwners } from '../queries/useElement';
import styles from './ElementScreen.module.css';

/**
 * Właściciele elementu — **z akcjami** (spec 9.1, decyzja 110).
 *
 * ⭐⭐ Do 2026-08-08 ta sekcja była **wyłącznie do odczytu**: właściciela dawało się nadać
 * tylko z omniboksa przy tworzeniu albo z listy roboczej po imporcie. Czyli mechanizm
 * własności — fundament uprawnień w całym produkcie (spec 9.2) — nie miał wejścia tam,
 * gdzie człowiek na niego patrzy.
 *
 * ⭐ **Trzy różne rzeczy, celowo rozdzielone**, bo mylenie ich kończy się akcją, która
 * „nie działa":
 * <ul>
 *   <li><b>dodanie</b> — dokłada właściciela lokalnego (własność jest zbiorem, nie polem);</li>
 *   <li><b>zdjęcie</b> — dotyczy wyłącznie <b>lokalnego</b>;</li>
 *   <li><b>wykluczenie</b> — dotyczy wyłącznie <b>odziedziczonego</b> i ⚠ schodzi na całe
 *   poddrzewo pod tym elementem.</li>
 * </ul>
 */
export function OwnersSection({ element }: { element: ElementDetail }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const [adding, setAdding] = useState(false);

  /*
   * ⭐⭐ Mutacje siedzą W SEKCJI, nie w wierszu — i to nie jest kwestia gustu. Wiersz
   * **usuwa sam siebie** optimistic UI, więc odmontowuje się, zanim żądanie wróci; TanStack
   * porzuca wtedy callbacki przekazane do `mutate`, a razem z nimi **toast z „Cofnij"**.
   * Zanim to przeniesienie powstało, cofnięcia nie dało się kliknąć ani razu.
   */
  const actions = useOwnerActions(element.id);
  const setOwners = useSetOwners();

  const busy = actions.remove.isPending || actions.exclude.isPending || actions.restore.isPending;

  function fail(error: unknown) {
    show({ tone: 'danger', message: describeError(error) });
  }

  /** ⚠ Wsad jest NIEPODZIELNY (dec. 97), więc odsyłamy KOMPLET lokalnych. */
  function localsPlus(extra: OwnerView) {
    return [
      ...element.owners
        .filter((owner) => owner.isLocal)
        .map((owner) => ({ ownerKind: owner.ownerKind, ownerId: owner.ownerId })),
      { ownerKind: extra.ownerKind, ownerId: extra.ownerId },
    ];
  }

  function removeOwner(owner: OwnerView) {
    actions.remove.mutate(
      { kind: owner.ownerKind, ownerId: owner.ownerId },
      {
        onSuccess: () =>
          show({
            message: t('graph.owner.removed', { name: owner.displayName }),
            action: {
              label: t('common.action.undo'),
              // ⭐ Cofnięcie to zwykła druga mutacja — stan sprzed zmiany znamy, bo wiersz
              // właśnie go niósł. Zachowujemy też jego flagę dziedziczenia.
              onAction: () =>
                setOwners.mutate(
                  { elementId: element.id, owners: localsPlus(owner), inherit: owner.inherit },
                  { onError: fail },
                ),
            },
          }),
        onError: fail,
      },
    );
  }

  function excludeOwner(owner: OwnerView) {
    const input = { kind: owner.ownerKind, ownerId: owner.ownerId };

    actions.exclude.mutate(input, {
      onSuccess: () =>
        show({
          // ⚠⚠ Komunikat MÓWI O PODDRZEWIE. Wykluczenie nie jest akcją punktową,
          // a z nazwy przycisku nikt się tego nie domyśli.
          message: t('graph.owner.excluded', { name: owner.displayName }),
          action: {
            label: t('common.action.undo'),
            onAction: () => actions.restore.mutate(input, { onError: fail }),
          },
        }),
      onError: fail,
    });
  }

  return (
    <section className={styles['section']} aria-labelledby="element-owners">
      <div className={styles['sectionHead']}>
        <h2 id="element-owners" className={styles['sectionTitle']}>
          {t('graph.element.owners')}
        </h2>

        {/* ⛔ Akcje rysowane tylko przy uprawnieniu — nie pokazujemy drzwi, które i tak
            są zamknięte. Kontrolę trzyma serwer przy każdym żądaniu (spec 9.2). */}
        {element.canManageOwners && !adding ? (
          <Button variant="ghost" onClick={() => setAdding(true)}>
            <Plus size={15} aria-hidden="true" />
            {t('graph.owner.add')}
          </Button>
        ) : null}
      </div>

      {adding ? <OwnerPicker element={element} onDone={() => setAdding(false)} /> : null}

      {element.owners.length > 0 ? (
        <ul className={styles['owners']}>
          {element.owners.map((owner) => (
            <OwnerRow
              key={`${owner.ownerKind}-${owner.ownerId}`}
              owner={owner}
              busy={busy}
              canManage={element.canManageOwners}
              onRemove={() => removeOwner(owner)}
              onExclude={() => excludeOwner(owner)}
            />
          ))}
        </ul>
      ) : (
        <p className={styles['empty']}>{t('graph.element.noOwners')}</p>
      )}
    </section>
  );
}

/** ⚠ Czysto prezentacyjny — mutacje siedzą w sekcji, patrz komentarz wyżej. */
function OwnerRow({
  owner,
  busy,
  canManage,
  onRemove,
  onExclude,
}: {
  owner: OwnerView;
  busy: boolean;
  canManage: boolean;
  onRemove: () => void;
  onExclude: () => void;
}) {
  const { t } = useTranslation();

  return (
    <li className={styles['owner']}>
      <span className={styles['badge']}>
        {owner.ownerKind === 'team' ? t('graph.owner.team') : t('graph.owner.user')}
      </span>

      <span
        className={
          accountStateClass(owner.ownerState, styles, 'ownerNameOff') ?? styles['ownerName']
        }
      >
        {owner.displayName}
      </span>

      {/* ⛔ Wyszarzenie NIE MOŻE być jedynym nośnikiem (dec. 58: kolor nigdy sam) — stąd
          dopisek. Tooltipów w produkcie nie ma, więc nie ma gdzie schować wyjaśnienia.
          ⚠ Dwa różne dopiski, bo to dwa różne fakty: konto z importu CZEKA na wejście,
          a nikt go nie wyłączał. */}
      {accountStateKey(owner.ownerState) ? (
        <span className={styles['provenance']}>{t(accountStateKey(owner.ownerState)!)}</span>
      ) : null}

      {owner.isLocal ? (
        // ⚠ „Dziedziczy w dół" pokazujemy TYLKO przy lokalnym: przy odziedziczonym mówiłoby
        // o cudzym przypisaniu u przodka, którego stąd i tak się nie zmienia.
        <span className={styles['provenance']}>
          {owner.inherit ? t('graph.owner.inheritsDown') : t('graph.owner.stopsHere')}
        </span>
      ) : (
        <span className={styles['provenance']}>
          {t('graph.owner.inheritedFrom', {
            source: owner.sourceElementTitle ?? `#${owner.sourceElementId}`,
          })}
        </span>
      )}

      {canManage ? (
        <span className={styles['ownerActions']}>
          {owner.isLocal ? (
            <Button variant="ghost" disabled={busy} onClick={onRemove}>
              <UserRoundMinus size={14} aria-hidden="true" />
              {t('graph.owner.remove')}
            </Button>
          ) : (
            <Button variant="ghost" disabled={busy} onClick={onExclude}>
              <Scissors size={14} aria-hidden="true" />
              {t('graph.owner.exclude')}
            </Button>
          )}
        </span>
      ) : null}
    </li>
  );
}

/**
 * Dodanie właściciela.
 *
 * ⚠ Wyszukiwanie idzie **serwerowo** — lista ludzi i zespołów w firmie na 100+ deweloperów
 * nie mieści się w pamięci ekranu, a filtrowanie lokalne kłamałoby przy pierwszym przekroczeniu
 * limitu odpowiedzi.
 */
function OwnerPicker({ element, onDone }: { element: ElementDetail; onDone: () => void }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const [query, setQuery] = useState('');
  const [inherit, setInherit] = useState(true);

  const org = useQuery(orgQueryOptions(query));
  const setOwners = useSetOwners();

  function pick(ref: OrgRef) {
    setOwners.mutate(
      {
        elementId: element.id,
        // ⭐ Wsad jest NIEPODZIELNY (decyzja 97), a własność to zbiór — więc wysyłamy
        // dotychczasowych LOKALNYCH plus nowego. Sam nowy skasowałby resztę.
        owners: [
          ...element.owners
            .filter((owner) => owner.isLocal)
            .map((owner) => ({ ownerKind: owner.ownerKind, ownerId: owner.ownerId })),
          { ownerKind: ref.kind, ownerId: ref.id },
        ],
        inherit,
      },
      {
        onSuccess: () => {
          onDone();
          show({ tone: 'success', message: t('graph.owner.added', { name: ref.name }) });
        },
        onError: (error) => show({ tone: 'danger', message: describeError(error) }),
      },
    );
  }

  return (
    <div className={styles['ownerPicker']}>
      <label className={styles['ownerPickerLabel']}>
        {t('graph.owner.searchLabel')}
        <input
          className={styles['ownerPickerInput']}
          type="search"
          value={query}
          placeholder={t('graph.owner.searchPlaceholder')}
          onChange={(event) => setQuery(event.target.value)}
          /*
           * Pole pojawia się WYŁĄCZNIE po kliknięciu „Dodaj właściciela", więc przeniesienie
           * ogniska spełnia intencję, a nie porywa ją.
           */
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </label>

      {/* ⭐ Dziedziczenie ustawia się PRZY PRZYPISANIU (spec 9.1) — i to jest jedyny moment,
          w którym człowiek o nim myśli. Domyślnie włączone, bo tak działa własność. */}
      <label className={styles['ownerInherit']}>
        <input type="checkbox" checked={inherit} onChange={(event) => setInherit(event.target.checked)} />
        {t('graph.owner.inheritLabel')}
      </label>

      {org.isError ? (
        <p className={styles['ownerPickerError']} role="alert">
          {describeError(org.error)}
        </p>
      ) : (
        <ul className={styles['ownerPickerResults']}>
          {(org.data ?? []).map((ref) => (
            <li key={`${ref.kind}-${ref.id}`}>
              <button
                type="button"
                className={styles['ownerPickerOption']}
                disabled={setOwners.isPending}
                onClick={() => pick(ref)}
              >
                {ref.kind === 'team' ? (
                  <UsersRound size={14} aria-hidden="true" />
                ) : (
                  <CircleUserRound size={14} aria-hidden="true" />
                )}
                {ref.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      <Button variant="ghost" onClick={onDone}>
        {t('common.action.cancel')}
      </Button>
    </div>
  );
}
